<script>
	/* eslint-disable svelte/no-at-html-tags */
	// NPM Modules
	import { createEventDispatcher } from 'svelte';

	export let label = undefined;
	export let name = '';
	export let className = '';
	export let action = 'add';
	export let singleSelection = false;
	export let altLabel = false;
	export let unselectedText = 'select...';
	export let disabled = false;
	export let options = [];
	export let checked = [];
	export let errors = [];
	export let bgColour = 'bg-gray-500';
	export let textColour = 'text-white';
	export let rounded = true;
	export let wrapLabel = true;
	export let wrapValue = false;

	const dispatch = createEventDispatcher();

	$: selectEnabled = !!options.length;
	$: selectedText = checked && options && getSelectedText();

	let open = false;

	// SVG Paths
	let addPath =
		'<path d="M12 0c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12-5.373-12-12-12zm7 14h-5v5h-4v-5h-5v-4h5v-5h4v5h5v4z"/>';
	let removePath =
		'<path d="M12 0c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12-5.373-12-12-12zm7 14h-14v-4h14v4z"/>';
	let chevronDownPath = '<path d="M0 7.33l2.829-2.83 9.175 9.339 9.167-9.339 2.829 2.83-11.996 12.17z" />';
	let chevronUpPath = '<path d="M0 16.67l2.829 2.83 9.175-9.339 9.167 9.339 2.829-2.83-11.996-12.17z"/>';
	let tickBoxPath = '<path d="M22 2v20h-20v-20h20zm2-2h-24v24h24v-24z"/></svg>';
	let tickedBoxPath =
		'<path d="M22 2v20h-20v-20h20zm2-2h-24v24h24v-24zm-5.541 8.409l-1.422-1.409-7.021 7.183-3.08-2.937-1.395 1.435 4.5 4.319 8.418-8.591z"/>';

	function onActionClicked() {
		dispatch(action);
	}

	function optionClicked(value) {
		if (disabled) {
			return;
		}
		if (singleSelection) {
			checked = [value];
			open = false;
		} else {
			let index = checked.indexOf(value);
			if (index >= 0) {
				// remove
				checked = [...checked.slice(0, index), ...checked.slice(index + 1)];
			} else {
				// add
				checked = [...checked, value];
			}
		}
		dispatch('change', { name, checked });
	}

	function isChecked(option) {
		return checked.indexOf(option) >= 0;
	}

	function getSingleValue() {
		try {
			let value = options.filter((item) => item[0] === checked[0]);
			return value[0] ? value[0][1] : unselectedText;
		} catch (error) {
			console.error(error);
		}
	}

	function getSelectedValueList() {
		try {
			let selectedString = '';
			for (const checkedOption of checked) {
				let value = options.filter((item) => item[0] === checkedOption);
				if (value[0]) {
					selectedString = selectedString + ', ' + value[0][1];
				}
			}
			return selectedString.substring(2);
		} catch (error) {
			console.error(error);
		}
	}

	function getSelectedText() {
		if (checked && checked.length === 1) {
			return getSingleValue();
		} else {
			return checked.length > 1 ? checked.length + ' selected' : unselectedText;
		}
	}
</script>

<svelte:window
	on:keydown={(e) => {
		if (e.key === 'Escape') open = false;
	}}
/>

<div class="relative flex flex-col m-1">
	{#if label && altLabel}
		<div class="pb-3 -mt-1">
			{label}
		</div>
	{/if}

	<div class="flex text-sm relative">
		<button
			type="button"
			disabled={!selectEnabled || (disabled && singleSelection) || (disabled && checked.length == 0)}
			on:click={() => (open = !open)}
			on:blur={() => (open = false)}
			title={checked.length > 0 ? 'Selected: ' + getSelectedValueList() : 'Nothing selected'}
			class="flex items-center p-1.5 text-white {bgColour}
      {textColour}
      {rounded ? 'rounded-full' : ''} {disabled
				? 'opacity-50'
				: ''} h-8 {className} focus:outline-none flex justify-between {selectEnabled && !disabled
				? 'cursor-pointer'
				: disabled
					? 'cursor-not-allowed'
					: 'cursor-default'}"
		>
			{#if action !== '' && action !== false}
				<button type="button" on:click|stopPropagation={onActionClicked} {disabled} class="mr-1 focus:outline-none">
					<svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 24 24">
						{@html action === 'add' ? addPath : removePath}
					</svg>
				</button>
			{/if}
			{#if label && !altLabel}
				<div
					class="{options ? 'mr-1' : 'mr-2'}
          {(action === false || action == '') && 'pl-1'} font-semibold flex {textColour}"
					class:whitespace-nowrap={wrapLabel}
				>
					{label}
				</div>
			{/if}
			{#if selectEnabled}
				<div class="flex items-center" class:w-full={altLabel}>
					<div class="mr-2 min-w-[3.5rem]" class:w-full={altLabel} class:whitespace-nowrap={wrapValue}>
						{selectedText}
					</div>
					<div class="mr-1 focus:outline-none">
						<svg class="w-2 h-2" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 24 24">
							{@html open ? chevronUpPath : chevronDownPath}
						</svg>
					</div>
				</div>
			{/if}
		</button>
		{#if errors && errors.length}
			<div class="pt-1 text-center text-red-500">{errors[0]}</div>
		{/if}
		{#if open}
			<div class="absolute z-50 flex min-w-full p-2 mt-10 bg-white rounded shadow-md">
				<ul class="flex flex-col text-sm text-nowrap w-full">
					{#each options as option}
						<li
							class="py-1 hover:bg-gray-200 {singleSelection &&
								isChecked(option[0]) &&
								'border-l-4 border-green-300 pl-1 -ml-2 font-semibold'}"
							class:hover:bg-gray-200={!disabled}
							class:cursor-pointer={!disabled}
							class:cursor-not-allowed={disabled}
							on:mousedown|preventDefault={() => optionClicked(option[0])}
						>
							<div class="select-none">
								{#if !singleSelection}
									<svg
										class="inline w-4 h-4 mr-2"
										xmlns="http://www.w3.org/2000/svg"
										fill="currentColor"
										viewBox="0 0 24 24"
									>
										{@html checked && isChecked(option[0]) ? tickedBoxPath : tickBoxPath}
									</svg>
								{/if}
								<span class="inline">{option[1]}</span>
							</div>
						</li>
					{/each}
				</ul>
			</div>
		{/if}
	</div>
</div>
